import { supabase } from '../../lib/supabase';
import type { PodcastRow } from '../../types/database';
import type { Podcast } from '../../types/index';

function mapRowToPodcast(row: PodcastRow): Podcast {
  return {
    id: row.id,
    title: row.title ?? '',
    description: row.description ?? '',
    channel_name: row.channel_name ?? '',
    channel_url: row.channel_url ?? '',
    thumbnail_url: row.thumbnail_url ?? undefined,
    subscriber_count: row.subscriber_count ?? undefined,
  };
}

/**
 * Get all podcasts (direct Supabase query).
 */
export async function getPodcasts(): Promise<Podcast[]> {
  const { data, error } = await supabase
    .from('podcasts')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching podcasts:', error);
    throw new Error(`Failed to fetch podcasts: ${error.message}`);
  }

  return (data ?? []).map(mapRowToPodcast);
}

/**
 * Get podcast by ID.
 */
export async function getPodcastById(id: string): Promise<Podcast | null> {
  const { data, error } = await supabase
    .from('podcasts')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch podcast: ${error.message}`);
  }

  return data ? mapRowToPodcast(data) : null;
}

/** UUID regex: 8-4-4-4-12 hex with dashes. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Extract podcast ID from route slug. Slugs are either:
 * - Plain UUID: "80b49228-ed0b-42bd-8ec7-237b93f37588"
 * - UUID + title slug: "80b49228-ed0b-42bd-8ec7-237b93f37588-greg-isenberg"
 */
export function extractPodcastIdFromSlug(slug: string): string {
  const match = slug.match(UUID_REGEX);
  return match ? match[0] : slug;
}

/**
 * Get podcast by channel URL, title, or ID (for slug/nameOrId routes).
 * Handles URL slugs like "uuid-title-slug" by extracting the UUID first.
 */
export async function getPodcastByNameOrId(nameOrId: string): Promise<Podcast | null> {
  const idFromSlug = extractPodcastIdFromSlug(nameOrId);
  const byId = await getPodcastById(idFromSlug);
  if (byId) return byId;

  const { data, error } = await supabase
    .from('podcasts')
    .select('*')
    .ilike('title', nameOrId)
    .limit(1)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    throw new Error(`Failed to fetch podcast: ${error.message}`);
  }

  return data ? mapRowToPodcast(data) : null;
}
